import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { scientificPartnerApi } from '@/features/workspace/api';
import {
  FileTextIcon,
  FolderKanbanIcon,
  VoteIcon,
  UsersIcon,
  BriefcaseIcon,
  RefreshCwIcon,
} from './components/PartnerIcons';
import { PartnerRequestsView } from './components/PartnerRequestsView';
import { PartnerProjectsView } from './components/PartnerProjectsView';
import { PartnerEngagementView } from './components/PartnerEngagementView';
import { PartnerClientOrdersView } from './components/PartnerClientOrdersView';
import { PartnerSharedArticlesView } from './components/PartnerSharedArticlesView';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { FileHubPage } from '@/features/file-hub';
import { CreateRequestEntry } from '@/features/request-gateway';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

export const ScientificPartnerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [summary, setSummary] = useState<any>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const isRootPath =
    location.pathname === '/app/partner' ||
    location.pathname === '/app/partner/' ||
    location.pathname === '/app/scientific-partner' ||
    location.pathname === '/app/scientific-partner/';

  const loadSummary = async () => {
    try {
      setRefreshing(true);
      const data = await scientificPartnerApi.summary();
      setSummary(data);
    } catch {
      setSummary({
        user_name: 'Алтын Бақытжанова',
        user_code: 'ZA000003',
        status_online: true,
        stats: {
          active_requests: 4,
          collab_projects: 4,
          events_polls: 8,
          client_orders: 5,
          total_revenue_kzt: 950000
        }
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleCreateRequest = () => {
    setShowCreateModal(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4 sm:p-6 lg:p-8 role-cabinet-page">
      <div className="partner-cabinet-shell space-y-6">
        <div className="partner-cabinet-hero rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-muted/20 p-5 sm:p-6 shadow-xs">
          <div className="role-hero" style={{ ['--role-accent' as string]: '#49ddb7' }}>
            <div className="flex items-start sm:items-center gap-4 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 text-white font-black text-xl flex items-center justify-center shadow-md">
                  АБ
                </div>
              </div>
              <div className="min-w-0">
                <small>{summary?.user_code || 'ZA000003'} · Научный партнёр</small>
                <h2 className="truncate">{summary?.user_name || 'Алтын Бақытжанова'}</h2>
                <p>Научный партнёр / коллега · PhD, Доцент института экономических исследований</p>
              </div>
            </div>
            <div className="role-hero-actions">
              <button
                type="button"
                onClick={loadSummary}
                disabled={refreshing}
                className="role-btn"
                title="Обновить данные"
              >
                <RefreshCwIcon size={16} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button type="button" onClick={handleCreateRequest} className="role-btn primary">
                + Создать заявку
              </button>
            </div>
          </div>
        </div>

        {isRootPath ? (
          <CabinetWorkspaceFrame
            links={{
              notebook: `${routes.partner}/files`,
              calendar: routes.newRequest,
              meetings: routes.portalSection('education'),
              connections: `${routes.partner}/academic_connections`,
              alliances: `${routes.partner}/academic_connections`,
              qa: routes.portalSection('instructions'),
              support: routes.portalSection('contacts'),
            }}
          >
            <div className="partner-center-stack">
              <div className="partner-kpi-grid">
                <button
                  type="button"
                  onClick={() => navigate('/app/partner/requests')}
                  className="partner-kpi-card"
                  style={{ ['--a' as string]: '#49ddb7' }}
                >
                  <span>Активные заявки</span>
                  <strong>{summary?.stats?.active_requests ?? 4}</strong>
                  <small>1 на согласовании</small>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/app/partner/projects')}
                  className="partner-kpi-card"
                  style={{ ['--a' as string]: '#41d2ff' }}
                >
                  <span>Совместные проекты</span>
                  <strong>{summary?.stats?.collab_projects ?? 4}</strong>
                  <small>1 грант МОН РК</small>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/app/partner/engagement')}
                  className="partner-kpi-card"
                  style={{ ['--a' as string]: '#9e80ff' }}
                >
                  <span>Мероприятия и опросы</span>
                  <strong>{summary?.stats?.events_polls ?? 8}</strong>
                  <small>3 на этой неделе</small>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/app/partner/client_orders')}
                  className="partner-kpi-card"
                  style={{ ['--a' as string]: '#e2ad3a' }}
                >
                  <span>Заказы и публикации</span>
                  <strong>{summary?.stats?.client_orders ?? 5}</strong>
                  <small>
                    {(summary?.stats?.total_revenue_kzt ?? 950000).toLocaleString('ru-RU')} ₸ оборот
                  </small>
                </button>
              </div>

              <div className="partner-modules">
                <header className="partner-modules-head">
                  <h2>Рабочие модули партнёра</h2>
                  <p>5 основных карточек кабинета научного партнёра / коллеги</p>
                </header>

                <div className="owner-hub-grid partner-hub-grid">
                  {[
                    {
                      path: '/app/partner/requests',
                      icon: FileTextIcon,
                      num: '01',
                      title: 'Мои заявки',
                      text: 'Активные, требующие уточнения, ожидающие подтверждения и завершённые заявки.',
                      accent: '#49ddb7',
                    },
                    {
                      path: '/app/partner/projects',
                      icon: FolderKanbanIcon,
                      num: '02',
                      title: 'Мои проекты',
                      text: 'Гранты, статьи, совместные исследования и завершённые научные результаты.',
                      accent: '#41d2ff',
                    },
                    {
                      path: '/app/partner/engagement',
                      icon: VoteIcon,
                      num: '03',
                      title: 'Опросы и мероприятия',
                      text: 'Входящие опросы, встречи и мероприятия от владельца.',
                      accent: '#9e80ff',
                    },
                    {
                      path: '/app/partner/academic_connections',
                      icon: UsersIcon,
                      num: '04',
                      title: 'Академические связи',
                      text: 'Переписка с владельцем, авторами, коллегами и разрешёнными категориями.',
                      accent: '#5da8ff',
                    },
                    {
                      path: '/app/partner/client_orders',
                      icon: BriefcaseIcon,
                      num: '05',
                      title: 'Заказы клиентов',
                      text: 'Заказы, клиенты, статьи, совместные публикации, файлы и связанные действия.',
                      accent: '#e2ad3a',
                    },
                  ].map((mod) => {
                    const Icon = mod.icon;
                    return (
                      <button
                        key={mod.path}
                        type="button"
                        onClick={() => navigate(mod.path)}
                        className="owner-hub-card"
                        style={{ ['--action' as string]: mod.accent }}
                      >
                        <span className="owner-hub-index">{mod.num}</span>
                        <span className="owner-hub-icon" aria-hidden="true">
                          <Icon size={22} />
                        </span>
                        <span className="owner-hub-copy">
                          <strong>{mod.title}</strong>
                          <span>{mod.text}</span>
                        </span>
                        <span className="owner-hub-arrow" aria-hidden="true">
                          ›
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <ScientificGpsBlock openTo={`${routes.partner}/projects`} />
            </div>
          </CabinetWorkspaceFrame>
        ) : (
          <Routes>
            <Route path="requests" element={<PartnerRequestsView />} />
            <Route path="projects" element={<PartnerProjectsView />} />
            <Route path="engagement" element={<PartnerEngagementView />} />
            <Route
              path="academic_connections"
              element={
                <RoleAcademicConnectionsOverlay
                  isOpen
                  onClose={() => navigate(routes.partner)}
                  roleContext="RESEARCH_PARTNER"
                  roleTitle="Научный партнёр"
                />
              }
            />
            <Route
              path="academic_connections/*"
              element={
                <RoleAcademicConnectionsOverlay
                  isOpen
                  onClose={() => navigate(routes.partner)}
                  roleContext="RESEARCH_PARTNER"
                  roleTitle="Научный партнёр"
                />
              }
            />
            <Route path="client_orders" element={<PartnerClientOrdersView />} />
            <Route
              path="files"
              element={
                <FileHubPage
                  onClose={() => navigate(routes.partner)}
                  onBack={() => navigate(routes.partner)}
                />
              }
            />
            <Route
              path="files/*"
              element={
                <FileHubPage
                  onClose={() => navigate(routes.partner)}
                  onBack={() => navigate(routes.partner)}
                />
              }
            />
            <Route path="articles" element={<PartnerSharedArticlesView />} />
          </Routes>
        )}
      </div>

      <CreateRequestEntry
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onBack={() => setShowCreateModal(false)}
        activeRole="MENTOR_PARTNER"
        roleLabel="Научный партнёр"
      />
    </div>
  );
};
